import React, { useState } from 'react';
import { format } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import StepPhoto from './StepPhoto';
import StepSkin from './StepSkin';
import StepRoutine from './StepRoutine';
import StepTreatments from './StepTreatments';
import StepSupplements from './StepSupplements';
import StepWater from './StepWater';
import StepMakeup from './StepMakeup';
import StepCycle from './StepCycle';
import SafeIcon from '../../common/SafeIcon';
import { FiChevronLeft, FiChevronRight, FiCheck, FiX } from 'react-icons/fi';

const STEPS = [
  { id: 'photo', label: 'Photo & Glow', emoji: '📸' },
  { id: 'skin', label: 'Skin Feel', emoji: '✨' },
  { id: 'water', label: 'Hydration', emoji: '💧' },
  { id: 'routine', label: 'Routine', emoji: '🧴' },
  { id: 'treatments', label: 'Treatments', emoji: '💉' },
  { id: 'supplements', label: 'Supplements', emoji: '💊' },
  { id: 'makeup', label: 'Makeup', emoji: '💄' },
  { id: 'cycle', label: 'Cycle', emoji: '🩸' },
];

export default function LogView({ onSave, onCancel, onOpenPricing }) {
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    photos: [],
    glowScore: 7,
    tags: [],
    water: 0,
    routines: { AM: [], PM: [] },
    treatments: [],
    supplements: [],
    notes: '',
    makeup: { wore: false, products: [], doubleCleansed: false, notes: '' },
    cycle: { periodDay: false, flow: null, symptoms: [] },
  });

  const update = (partial) => setFormData(prev => ({ ...prev, ...partial }));
  const isLast = step === STEPS.length - 1;

  const handleNext = () => {
    if (isLast) {
      onSave({ ...formData, id: Date.now() });
    } else {
      setStep(s => s + 1);
    }
  };

  const handleBack = () => {
    if (step === 0) {
      onCancel();
    } else {
      setStep(s => s - 1);
    }
  };

  const renderStep = () => {
    switch (STEPS[step].id) {
      case 'photo': return <StepPhoto data={formData} onChange={update} onOpenPricing={onOpenPricing} />;
      case 'skin': return <StepSkin data={formData} onChange={update} />;
      case 'water': return <StepWater data={formData} onChange={update} />;
      case 'routine': return <StepRoutine data={formData} onChange={update} />;
      case 'treatments': return <StepTreatments data={formData} onChange={update} />;
      case 'supplements': return <StepSupplements data={formData} onChange={update} />;
      case 'makeup': return <StepMakeup data={formData} onChange={update} />;
      case 'cycle': return <StepCycle data={formData} onChange={update} />;
      default: return null;
    }
  };

  return (
    <div className="h-full flex flex-col relative z-10">
      {/* Header */}
      <div className="px-5 pt-8 pb-4 flex-shrink-0">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={handleBack}
            className="w-10 h-10 rounded-full glass-input flex items-center justify-center text-slate-800 active:bg-white/50"
          >
            <SafeIcon icon={FiChevronLeft} className="text-xl" />
          </button>
          
          <div className="text-center">
            <p className="font-sans text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">
              {STEPS[step].emoji} Step {step + 1} of {STEPS.length}
            </p>
            <h2 className="font-sans text-2xl font-medium text-slate-900 tracking-tight">{STEPS[step].label}</h2>
          </div>
          
          <button
            onClick={onCancel}
            className="w-10 h-10 rounded-full glass-input flex items-center justify-center text-slate-800 active:bg-white/50"
          >
            <SafeIcon icon={FiX} className="text-xl" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="flex gap-1.5 px-2">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className="flex-1 h-1.5 rounded-full transition-all duration-300"
              style={{
                background: i <= step ? '#0f172a' : 'rgba(255, 255, 255, 0.6)',
              }}
            />
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="flex-1 overflow-y-auto px-5 py-5">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Footer */}
      <div className="px-5 pb-8 pt-4 flex-shrink-0 glass-panel">
        <motion.button
          whileTap={{ scale: 0.97 }}
          onClick={handleNext}
          className="w-full py-4 rounded-[2rem] font-sans font-medium text-white text-base flex items-center justify-center gap-2 bg-slate-900 shadow-lg"
        >
          {isLast ? (
            <><SafeIcon icon={FiCheck} /> Save Entry</>
          ) : (
            <>Continue <SafeIcon icon={FiChevronRight} /></>
          )}
        </motion.button>
        
        {!isLast && (
          <button
            onClick={handleNext}
            className="w-full text-center text-sm text-slate-500 font-sans mt-4 py-1 active:text-slate-800 transition-colors"
          >
            Skip this step
          </button>
        )}
      </div>
    </div>
  );
}